import { useCallback, useEffect, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { IRadialMenuItem, RadialMenuIcon } from '~/shared';
import { radialAnim, radialIcons, zoneMap } from './RadialMenu.model';
import {
	StyledContainer,
	StyledEmptyMenuItemContainer,
	StyledItemsCircle,
	StyledMenuItemContainer,
	StyledSelectorContainer,
} from './RadialMenu.styled';

const RadialMenu: React.FC = () => {
	const items = useObservable(rage.radialMenu.getItems());
	const [selectedItem, setSelectedItem] = useState<IRadialMenuItem | null>(
		null,
	);
	const [hoveredItem, setHoveredItem] = useState<IRadialMenuItem | null>(null);
	const [mousePosition, setMousePosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [menuStack, setMenuStack] = useState<(IRadialMenuItem | null)[][]>([]);
	const [currentItems, setCurrentItems] = useState<(IRadialMenuItem | null)[]>(
		[],
	);
	const [isAnimating, setIsAnimating] = useState(false);
	const [isExpanding, setIsExpanding] = useState(false);
	const [menuDepth, setMenuDepth] = useState(0);

	const createBackButton = useCallback(
		(): IRadialMenuItem => ({
			action: 'back',
			icon: RadialMenuIcon.Exit,
			text: 'Назад',
			subText: '',
			nestedItems: null,
		}),
		[],
	);

	const prepareItemsWithBack = useCallback(
		(items: IRadialMenuItem[]): (IRadialMenuItem | null)[] => {
			if (items.length === 0) return items;

			const result = [...items];

			let backPosition: number;

			if (items.length <= 2) {
				backPosition = 3;
				while (result.length < 3) {
					result.push({
						action: '',
						icon: RadialMenuIcon.ToggleLock,
						text: '',
						subText: '',
						nestedItems: null,
					});
				}
			} else if (items.length <= 6) {
				backPosition = 5;
			} else if (items.length <= 8) {
				backPosition = 7;
			} else {
				backPosition = result.length;
			}

			result.splice(backPosition, 0, createBackButton());

			if (result.length % 2 !== 0) {
				result.push({
					action: '',
					icon: RadialMenuIcon.ToggleLock,
					text: '',
					subText: '',
					nestedItems: null,
				});
			}

			return result;
		},
		[createBackButton],
	);

	const evenCount = useMemo(() => {
		const itemsToCount = currentItems.length > 0 ? currentItems : items;
		if (itemsToCount.length % 2 === 0) {
			return itemsToCount.length;
		}

		return itemsToCount.length + 1;
	}, [currentItems.length, items.length]);

	const animateMenuTransition = useCallback(
		async (newItems: (IRadialMenuItem | null)[], isForward: boolean = true) => {
			setIsExpanding(false);
			setIsAnimating(true);
			const collapseDurationMs =
				(evenCount - 1) * radialAnim.stepDelayMs +
				radialAnim.transformMs +
				radialAnim.bufferMs;
			await new Promise((resolve) => setTimeout(resolve, collapseDurationMs));

			setCurrentItems(newItems);
			setMenuDepth((prev) => (isForward ? prev + 1 : prev - 1));
			setIsExpanding(true);
			await new Promise((resolve) => setTimeout(resolve, radialAnim.domTickMs));

			setIsAnimating(false);
			const newLength = newItems.length;
			const newEvenCount = newLength % 2 === 0 ? newLength : newLength + 1;
			const expandDurationMs =
				(newEvenCount - 1) * radialAnim.stepDelayMs +
				radialAnim.transformMs +
				radialAnim.bufferMs;
			await new Promise((resolve) => setTimeout(resolve, expandDurationMs));
			setIsExpanding(false);
		},
		[evenCount],
	);

	const sectorAngle = useMemo(() => {
		return 360 / evenCount;
	}, [evenCount]);

	const strokeDasharray = useMemo(() => {
		const circumference = 2 * Math.PI * 24;
		const sectorLength = (circumference * sectorAngle) / 360;
		return `${sectorLength} ${circumference}`;
	}, [sectorAngle]);

	const cursorAngle = useMemo(() => {
		if (!mousePosition) return 0;

		const centerX = window.innerWidth / 2;
		const centerY = window.innerHeight / 2;

		const deltaX = mousePosition.x - centerX;
		const deltaY = mousePosition.y - centerY;

		let angle = Math.atan2(deltaY, deltaX);

		angle = (angle * 180) / Math.PI;

		angle = (angle + 360) % 360;

		return angle;
	}, [mousePosition]);

	const blockZones = useMemo(() => {
		return zoneMap[evenCount] || {};
	}, [evenCount]);

	useEffect(() => {
		if (items.length > 0) {
			const result = [...items];
			if (result.length % 2 !== 0) {
				result.push({
					action: '',
					icon: RadialMenuIcon.ToggleLock,
					text: '',
					subText: '',
					nestedItems: null,
				});
			}

			setCurrentItems(result);
			setMenuStack([]);
		}
	}, [items]);

	useEffect(() => {
		const handleMouseMove = (event: MouseEvent) => {
			setMousePosition({ x: event.clientX, y: event.clientY });
		};

		document.addEventListener('mousemove', handleMouseMove);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
		};
	}, []);

	useEffect(() => {
		setHoveredItem(null);
	}, [currentItems, menuDepth]);

	useEffect(() => {
		if (!mousePosition || currentItems.length === 0) {
			setSelectedItem(null);
			return;
		}

		let selectedIndex = -1;

		for (let i = 0; i < evenCount; i++) {
			const item = currentItems[i];
			if (item && item.action !== '' && item.text !== '') {
				const zone = blockZones[i];
				if (!zone) continue;

				let inZone = false;

				if (zone.start <= zone.end) {
					// Обычная зона
					inZone = cursorAngle >= zone.start && cursorAngle <= zone.end;
				} else {
					// Зона через 0°
					inZone = cursorAngle >= zone.start || cursorAngle <= zone.end;
				}

				if (inZone) {
					selectedIndex = i;
					break;
				}
			}
		}

		if (selectedIndex !== -1) {
			setSelectedItem(currentItems[selectedIndex]);
		} else {
			setSelectedItem(null);
		}
	}, [cursorAngle, blockZones, currentItems, evenCount]);

	const handleSelect = useCallback(
		async (itemOverride?: IRadialMenuItem | null) => {
			const item = itemOverride ?? selectedItem;
			if (!item) {
				await rage.radialMenu.exit();
				return;
			}

			if (item.action === 'back') {
				if (menuStack.length > 0) {
					const previousItems = menuStack[menuStack.length - 1];
					setMenuStack(menuStack.slice(0, -1));
					await animateMenuTransition(previousItems, false);
				} else {
					await rage.radialMenu.exit();
				}
				return;
			}

			if (item.nestedItems && item.nestedItems.length > 0) {
				const newItems = prepareItemsWithBack(item.nestedItems);
				setMenuStack([...menuStack, currentItems]);
				await animateMenuTransition(newItems, true);
				return;
			}

			await rage.radialMenu.selectItem(item.action);
			await rage.radialMenu.exit();
		},
		[selectedItem, menuStack, currentItems, prepareItemsWithBack],
	);

	const handleClick = useCallback(
		async (item: IRadialMenuItem | null) => {
			if (!item) return;
			await handleSelect(item);
		},
		[handleSelect],
	);

	useEffect(() => {
		const handleKeyPress = (event: KeyboardEvent) => {
			const key = event.key;
			const keyNumber = parseInt(key);

			if (keyNumber >= 1 && keyNumber <= currentItems.length) {
				const item = currentItems[keyNumber - 1];
				if (item && item.action !== '' && item.text !== '') {
					handleClick(item).catch((err) =>
						rage.notifications.showException(err),
					);
				}
			}
		};

		document.addEventListener('keydown', handleKeyPress);

		return () => {
			document.removeEventListener('keydown', handleKeyPress);
		};
	}, [currentItems, handleClick]);

	return (
		<StyledContainer>
			<StyledItemsCircle className={`n-${evenCount}`}>
				{new Array(evenCount).fill(0).map((_, index) => {
					const item = currentItems?.[index];
					if (item && item.action !== '' && item.text !== '') {
						return (
							<StyledMenuItemContainer
								key={`${item.action}-${menuDepth}`}
								$selected={hoveredItem === item}
								$isAnimating={isAnimating}
								$index={index}
								$evenCount={evenCount}
								$isExpanding={isExpanding}
								onMouseEnter={() => {
									setHoveredItem(item);
									playSound(Sound.LinkCursor);
								}}
								onMouseLeave={() => setHoveredItem(null)}
								onClick={() => {
									handleClick(item);
									playSound(Sound.ClickButton);
								}}
							>
								<img src={radialIcons[item.icon]} />
								<Text>
									{item.text} <span>{item.subText}</span>
								</Text>
								<div className="key">
									<Text>{index + 1}</Text>
								</div>
							</StyledMenuItemContainer>
						);
					}

					return (
						<StyledEmptyMenuItemContainer
							key={`empty-${index}-${menuDepth}`}
							$isAnimating={isAnimating}
							$index={index}
							$evenCount={evenCount}
							$isExpanding={isExpanding}
						/>
					);
				})}
			</StyledItemsCircle>
			<StyledSelectorContainer width={60} height={60}>
				<circle
					cx={30}
					cy={30}
					r={24}
					strokeWidth={12}
					stroke={'#ffffff'}
					fill="none"
					opacity={0.1}
				/>
				<circle
					cx={30}
					cy={30}
					r={24}
					strokeWidth={12}
					stroke={'#ffffff'}
					fill="none"
					strokeDasharray={strokeDasharray}
					strokeDashoffset={0}
					transform={`rotate(${cursorAngle - 360 / evenCount / 2} 30 30)`}
				/>
			</StyledSelectorContainer>
		</StyledContainer>
	);
};

export { RadialMenu };
